import { Alert, AlertColor, Box, Grid, Snackbar } from '@mui/material'
import MuiPhoneForm from 'components/Common/Phone/muiPhoneForm'
import SectionHeader from 'components/Common/SectionHeader'
import Form from 'components/RouteWizard/Form'
import Input from 'components/RouteWizard/Input'
import { useMemo, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getUser, updateUser } from 'reducers/usersReducer'
import schema from './schema'

type UserClient = {
  company: String
}

type ContactInfo = {
  mobileNumber?: string
  officeNumber?: string
}

type UserInfo = {
  userId: number
  firstName: string
  lastName: string
  email?: string
  contact?: ContactInfo
  delegates?: any[]
  travelPolicyId?: number | null
}

type UserDetail = {
  company: UserClient
  userDetail: UserInfo
}

export const UpdateUser = (props: UserDetail) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [alertMessage, setAlertMessage] = useState('')
  const [alertType, setAlertType] = useState<AlertColor>()
  const [showAlert, setShowAlert] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const formMethods = useRef<UseFormReturn | null>(null)
  const origMobile = useRef(props.userDetail.contact?.mobileNumber ?? '')
  const origOffice = useRef(props.userDetail.contact?.officeNumber ?? '')
  const mobileRef = useRef(origMobile.current)
  const officeRef = useRef(origOffice.current)

  const onSubmit = async (data: any) => {
    const isValid = (await formMethods.current?.trigger()) ?? true
    if (!isValid) {
      return
    }

    setIsLoading(true)
    const changedUser: any = {}

    if (data.firstName !== props.userDetail.firstName) {
      changedUser.firstName = data.firstName
    }
    if (data.lastName !== props.userDetail.lastName) {
      changedUser.lastName = data.lastName
    }

    if (mobileRef.current !== origMobile.current) {
      changedUser.contact = changedUser.contact || {}
      changedUser.contact.mobileNumber = mobileRef.current
    }
    if (officeRef.current !== origOffice.current) {
      changedUser.contact = changedUser.contact || {}
      changedUser.contact.officeNumber =
        officeRef.current && officeRef.current.length < 5
          ? 'Removed'
          : officeRef.current
    }

    if (!Object.keys(changedUser).length) {
      setShowAlert(true)
      setAlertMessage('No updates made')
      setAlertType('error')
      setIsLoading(false)
      return
    }

    const updatedUser: any = {
      ...changedUser,
      travelPolicyId: props.userDetail.travelPolicyId,
      delegates: props.userDetail.delegates,
    }

    try {
      await updateUser({
        updatedUser,
        userid: data.userId,
      })
      history.replace(history.location.pathname)
      setShowAlert(true)
      setAlertMessage('Profile Updated')
      setAlertType('success')
      dispatch(getUser())
      setIsLoading(false)
    } catch (error) {
      setShowAlert(true)
      setAlertMessage('No updates made')
      setAlertType('error')
      setIsLoading(false)
    }
  }

  const defaultValues = useMemo(() => ({
    company: props.company,
    email: props.userDetail.email,
    firstName: props.userDetail.firstName,
    lastName: props.userDetail.lastName,
    contact: props.userDetail.contact,
    userId: props.userDetail.userId,
  }), [
    props.company,
    props.userDetail.email,
    props.userDetail.firstName,
    props.userDetail.lastName,
    props.userDetail.contact,
    props.userDetail.userId,
  ])

  const handleAlertClose = () => setShowAlert(false)

  return (
    <>
      <Box
        component="div"
        sx={{
          width: '100%',
          fontSize: '14px',
          backgroundColor: '#F8F8F8',
          color: '#8E8E8E',
          display: 'flex',
          flexShrink: 1,
          flexDirection: 'column',
          alignItems: 'center',
          paddingBottom: '20px',
        }}
      >
        <Grid
          container
          style={{
            width: '90%',
            display: 'block',
            textAlign: 'center',
            paddingTop: '20px',
          }}
        >
          <Form
            isLoading={isLoading}
            schema={schema}
            defaultValues={defaultValues}
            ref={formMethods}
            onSubmit={onSubmit}
            submitText="Update"
          >
            <SectionHeader title="Update Profile" />
            <Input name="company" label="Company" disabled />
            <Input name="email" label="Email Address" disabled />
            <Input name="firstName" label="First Name" />
            <Input name="lastName" label="Last Name" />

            <MuiPhoneForm
              name="contact.mobileNumber"
              label="Mobile Number"
              fullWidth
              onPhoneChange={(phone) => { mobileRef.current = phone }}
            />
            <MuiPhoneForm
              name="contact.officeNumber"
              label="Office Number"
              fullWidth
              onPhoneChange={(phone) => { officeRef.current = phone }}
            />
          </Form>
        </Grid>
      </Box>
      <Snackbar
        open={showAlert}
        autoHideDuration={6000}
        onClose={handleAlertClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={handleAlertClose}
          severity={alertType}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default UpdateUser
